/**
 * Delete Position Account Handler - 删除持仓账户处理器
 *
 * 负责处理 deletePositionAccount API 的完整逻辑
 */

import log from "logbeacon";
import type { OperationResult, UserData } from "../types";

import type { PositionsAccountService } from "../PositionsAccountService";

export class DeletePositionAccountHandler {
  constructor(
    private positionsAccountService: PositionsAccountService | null,
    private getCurrentUserId: () => string | null,
    private getOrCreateUserData: (userId: string) => UserData,
    private updateStateUserFolders: (
      filter: (accountId: string) => boolean,
    ) => void,
    private handleError: (error: Error) => void,
  ) {}

  /**
   * Execute delete position account
   * @param accountId 账户ID
   * @returns Promise<OperationResult> 操作结果
   */
  async execute(accountId: string): Promise<OperationResult> {
    if (!this.positionsAccountService || !accountId) {
      log.warn(
        "[DeletePositionAccountHandler] PositionsAccountService not initialized or accountId is empty",
      );
      return {
        success: false,
        error: "Service not initialized or accountId is empty",
      };
    }

    try {
      // 调用 API 删除账户
      const result =
        await this.positionsAccountService.deletePositionAccount(accountId);

      if (result.success) {
        // 删除成功后，更新缓存：从 userFolders 中移除该账户
        this.updateStateUserFolders((id) => id !== accountId);

        // 同时更新源数据
        const currentUserId = this.getCurrentUserId();
        if (currentUserId) {
          const userData = this.getOrCreateUserData(currentUserId);
          if (userData.userFolders) {
            userData.userFolders = userData.userFolders.filter(
              (folder) => folder.id !== accountId,
            );
          }
        }

        log.debug(
          `[DeletePositionAccountHandler] Successfully deleted position account: ${accountId}`,
        );
      }

      return {
        success: result.success,
        error: result.success ? undefined : result.message,
        message: result.message,
      };
    } catch (error) {
      log.error(
        "[DeletePositionAccountHandler] Failed to delete position account:",
        error,
      );
      this.handleError(error as Error);
      return {
        success: false,
        error: (error as Error).message || "Unknown error",
      };
    }
  }
}
